/**
 * useServiceQueue — exposes pending queue items for a vehicle and drains them.
 *
 * Security (§00):
 *   - uid guard: drain NEVER POSTs an item whose uid !== auth.currentUser.uid.
 *   - Single-flight mutex: module-level flag prevents two concurrent drains.
 *
 * Drain triggers (§00 robust — navigator.onLine is unreliable on flaky nets):
 *   - `online` event
 *   - `visibilitychange` / window focus
 *   - Periodic tick (30s) when online and queue has pending items
 *
 * PII-safe logging: catch blocks log only clientMutationId, vehicleId, status,
 * error code/httpStatus. Never the entry body or raw ApiError.
 *
 * Spec 011 — before/after photo refs: an entry with `pendingPhotoRefs` (see
 * serviceQueue.ts) is held here (left "pending", the POST attempt below is
 * skipped for it this pass) until every attached photo is known to be
 * committed. Resolution has two hops, mirroring the actual upload pipeline:
 * photoQueue clientId -> photoId (upload+commit done — checked against the
 * live photo queue, since usePhotoQueue.ts's drain also writes this back
 * directly once it happens) -> committed status (async strip-worker done —
 * checked via GET /v1/photos, polled once per drain pass same as everything
 * else here). lamport's revised DTO: the client only ever sends the plain
 * photoId once committed — the server resolves it to its authoritative
 * {photoId, sha256} internally, so this drain never builds or sees a sha256.
 * A photo that's discarded/unrecoverable before it ever gets a photoId is
 * dropped from the entry rather than blocking it forever; a photo that's
 * merely slow (still uploading, or strip still running) keeps the entry
 * queued and this drain's normal reconnect/30s-tick/focus triggers are what
 * eventually retry it — no separate polling loop needed.
 *
 * 409 handling: see useAddServiceEntry.ts's file header — same re-upload
 * framing applies here (keyed on `error.code === "PHOTO_NOT_READY"`, never
 * bare HTTP 409 — a 409 can also mean an idempotency conflict), just
 * persisted as `failureReason: 'photo_not_ready'` on the queue entry so the
 * ledger row (ServiceLog.tsx) can show it instead of the generic "not saved"
 * copy.
 *
 * False-label fix (task #43, founder-approved, contract frozen by lamport) —
 * ServiceLog.tsx's submit-time gate only ever queues an entry with
 * `laborHoursSource: 'photo_estimated' | 'edited'` when both before/after
 * slots were populated at submit, but a *queued* entry's slots (still
 * `pendingPhotoRefs` at that point) can still be abandoned before this drain
 * ever resolves them — the photo gets deleted, discarded, or dropped by the
 * photoQueue cap before it ever lands a committed photoId. Sealing the entry
 * with a photo-based provenance label it can no longer back would be a false
 * claim on the append-only ledger, AND the server's matching gate now 400s
 * it outright. So once `resolvePendingPhotoRefs` finishes a pass (not
 * blocked) for an entry that HAD `pendingPhotoRefs`, and one or both slots
 * never resolved to a committed photoId, the label is downgraded to
 * 'manual' (needs no photos) and both photo ids are dropped — the
 * `laborHours` NUMBER itself is untouched, since it's still a real
 * owner-entered duration; only the provenance CLAIM is no longer backed.
 */

import { PhotoListSchema, type Photo } from '@/hooks/usePhotos';
import { ApiError, apiFetch } from '@/lib/api';
import { auth } from '@/lib/firebase';
import type { StoredPhotoItem } from '@/lib/photoQueue';
import { ServiceEntrySchema } from '@/lib/schemas';
import {
  listFor,
  PHOTO_NOT_READY_CODE,
  remove,
  subscribe,
  update,
  type PendingPhotoRef,
  type QueuedEntry,
} from '@/lib/serviceQueue';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { NewServiceEntry } from './useAddServiceEntry';
import { useOnlineStatus } from './useOnlineStatus';

// Module-level single-flight flag — shared across all hook instances so two
// tabs / two mounted components can't double-drain the same item.
let draining = false;

/** Result of attempting to resolve a single before/after slot this pass. */
interface SlotResolution {
  /** Next pending state for this slot — undefined once resolved or abandoned. */
  next: PendingPhotoRef | undefined;
  /** Set to the committed photoId once the slot is fully resolved. */
  resolved?: string;
  /** True if the slot is still outstanding (not resolved, not abandoned). */
  blocked: boolean;
}

/**
 * Resolves one before/after slot as far as this drain pass can take it.
 * `committedPhotos` is null when GET /v1/photos couldn't be reached this
 * pass — the slot's photoId (if any) is preserved for the next attempt.
 */
function resolveSlot(
  ref: PendingPhotoRef | undefined,
  photoQueueItems: StoredPhotoItem[],
  committedPhotos: Photo[] | null,
): SlotResolution {
  if (!ref) return { next: undefined, blocked: false };

  let photoId = ref.photoId;
  if (!photoId) {
    const match = photoQueueItems.find((p) => p.clientId === ref.clientId);
    if (match?.photoId) {
      // Upload+commit already succeeded but the write-back in
      // usePhotoQueue.ts's drain hasn't landed here yet (race between the
      // two queues' drains) — upgrade now so we don't wait an extra pass.
      photoId = match.photoId;
    } else if (match) {
      // Still queued/uploading, or failed-but-not-yet-discarded — keep
      // waiting rather than guessing; the user can still retry a failed
      // upload from the slots UI.
      return { next: ref, blocked: true };
    } else {
      // Gone from photoQueue with no photoId ever recorded (discarded, or
      // dropped by the 50-item cap). Unrecoverable — abandon this slot
      // rather than blocking the entry on a photo that's never coming.
      return { next: undefined, blocked: false };
    }
  }

  if (!committedPhotos) {
    return { next: { photoId }, blocked: true };
  }

  const photo = committedPhotos.find((p) => p.photoId === photoId);
  if (photo?.status === 'committed') {
    return { next: undefined, resolved: photo.photoId, blocked: false };
  }
  // Uploaded but the async strip-worker hasn't finished yet (still 'pending'
  // in the album), or not visible in the list at all yet. Keep waiting.
  return { next: { photoId }, blocked: true };
}

/**
 * Attempts to resolve every pending before/after slot on a queued entry.
 * Returns the (possibly photo-augmented) entry body, the remaining pending
 * state to persist, and whether the entry must stay queued this pass.
 */
async function resolvePendingPhotoRefs(item: QueuedEntry): Promise<{
  entry: NewServiceEntry;
  pendingPhotoRefs: QueuedEntry['pendingPhotoRefs'];
  blocked: boolean;
}> {
  const pending = item.pendingPhotoRefs;
  if (!pending || (!pending.before && !pending.after)) {
    return { entry: item.entry, pendingPhotoRefs: undefined, blocked: false };
  }

  const { listAll: listPhotoQueue } = await import('@/lib/photoQueue');
  const photoQueueItems = await listPhotoQueue();

  // Only worth a network round-trip if at least one slot already has (or
  // just gained) a photoId to look up.
  const needsPhotoLookup = (role: 'before' | 'after'): boolean => {
    const ref = pending[role];
    if (!ref) return false;
    if (ref.photoId) return true;
    return !!photoQueueItems.find((p) => p.clientId === ref.clientId)?.photoId;
  };

  let committedPhotos: Photo[] | null = null;
  if (needsPhotoLookup('before') || needsPhotoLookup('after')) {
    try {
      const data = await apiFetch(`/v1/photos?vehicleId=${encodeURIComponent(item.vehicleId)}`, {
        schema: PhotoListSchema,
      });
      committedPhotos = data.photos;
    } catch {
      // Network/API hiccup — resolveSlot treats committedPhotos === null as
      // "keep the photoId, retry the committed-status lookup next pass".
      committedPhotos = null;
    }
  }

  const before = resolveSlot(pending.before, photoQueueItems, committedPhotos);
  const after = resolveSlot(pending.after, photoQueueItems, committedPhotos);

  const entry: NewServiceEntry = { ...item.entry };
  if (before.resolved) entry.beforePhotoId = before.resolved;
  if (after.resolved) entry.afterPhotoId = after.resolved;

  const nextPending: NonNullable<QueuedEntry['pendingPhotoRefs']> = {};
  if (before.next) nextPending.before = before.next;
  if (after.next) nextPending.after = after.next;
  const hasRemaining = !!(nextPending.before || nextPending.after);

  return {
    entry,
    pendingPhotoRefs: hasRemaining ? nextPending : undefined,
    blocked: before.blocked || after.blocked,
  };
}

async function drainQueue(vehicleId?: string): Promise<void> {
  if (draining) return;
  if (!navigator.onLine) return;

  const currentUser = auth.currentUser;
  if (!currentUser) return;

  draining = true;
  try {
    // Drain all pending items (not just for one vehicle) so reconnect clears
    // the whole backlog; callers can pass vehicleId to limit scope if needed,
    // but for global drain we list all items matching the current user.
    const { listAll } = await import('@/lib/serviceQueue');
    const all = await listAll();
    const targets = all.filter(
      (q) =>
        q.status === 'pending' &&
        // uid guard (§00 SECURITY): never POST another user's entry.
        q.uid === currentUser.uid &&
        // If vehicleId provided, restrict to that vehicle.
        (vehicleId === undefined || q.vehicleId === vehicleId),
    );

    for (const item of targets) {
      // Re-check auth (user could sign out mid-drain).
      if (auth.currentUser?.uid !== item.uid) continue;

      // Spec 011 — resolve before/after photo refs as far as this pass can
      // take them BEFORE attempting the POST. Persist any partial progress
      // (photoId learned, sha256 learned, or a slot abandoned) even when the
      // entry is still blocked, so later passes don't repeat completed work.
      const { entry, pendingPhotoRefs, blocked } = await resolvePendingPhotoRefs(item);
      if (blocked) {
        await update(item.clientMutationId, { entry, pendingPhotoRefs });
        continue;
      }
      if (item.pendingPhotoRefs) {
        // False-label fix (task #43) — resolution above just finished (not
        // blocked) for an entry that went through pendingPhotoRefs; if it
        // claimed a photo-backed labor-hours provenance but a slot was
        // abandoned along the way (deleted, discarded, or cap-evicted before
        // ever committing), that claim is now false and the server's
        // matching gate would reject it. Downgrade rather than seal a false
        // label or fail the entry outright — see file header.
        if (
          (entry.laborHoursSource === 'photo_estimated' || entry.laborHoursSource === 'edited') &&
          !(entry.beforePhotoId && entry.afterPhotoId)
        ) {
          delete entry.laborHoursSource;
          delete entry.beforePhotoId;
          delete entry.afterPhotoId;
        }
        await update(item.clientMutationId, { entry, pendingPhotoRefs });
      }

      await update(item.clientMutationId, { status: 'inflight' });

      try {
        await apiFetch(`/v1/vehicles/${item.vehicleId}/service`, {
          method: 'POST',
          body: {
            summary: entry.summary,
            clientMutationId: item.clientMutationId,
            ...(entry.performedAt ? { performedAt: entry.performedAt } : {}),
            ...(typeof entry.mileage === 'number' ? { mileage: entry.mileage } : {}),
            ...(entry.systems && entry.systems.length > 0 ? { systems: entry.systems } : {}),
            ...(entry.notes ? { notes: entry.notes } : {}),
            ...(entry.performedBy ? { performedBy: entry.performedBy } : {}),
            ...(typeof entry.laborHours === 'number' ? { laborHours: entry.laborHours } : {}),
            ...(entry.laborHoursSource ? { laborHoursSource: entry.laborHoursSource } : {}),
            ...(entry.source ? { source: entry.source } : {}),
            ...(entry.vendor ? { vendor: entry.vendor } : {}),
            ...(entry.invoiceImageRef ? { invoiceImageRef: entry.invoiceImageRef } : {}),
            ...(entry.lineItems ? { lineItems: entry.lineItems } : {}),
            ...(typeof entry.totalMinor === 'number' ? { totalMinor: entry.totalMinor } : {}),
            ...(entry.currency ? { currency: entry.currency } : {}),
            ...(entry.supersedesEntryId ? { supersedesEntryId: entry.supersedesEntryId } : {}),
            ...(entry.beforePhotoId ? { beforePhotoId: entry.beforePhotoId } : {}),
            ...(entry.afterPhotoId ? { afterPhotoId: entry.afterPhotoId } : {}),
            ...(typeof entry.selfPerformed === 'boolean'
              ? { selfPerformed: entry.selfPerformed }
              : {}),
          },
          schema: ServiceEntrySchema,
        });

        // 2xx (200 or 201): sealed — remove from queue.
        await remove(item.clientMutationId);
      } catch (err) {
        const isApiError = err instanceof ApiError;
        const httpStatus = isApiError ? err.status : undefined;
        const code = isApiError ? err.code : undefined;

        if (isApiError && err.code === PHOTO_NOT_READY_CODE) {
          // Photo not yet committed/stripped despite our own gating (race),
          // or a pre-cutover photo that never will be — see file header.
          // Terminal: won't retry the same id, surfaced as a re-upload
          // prompt (lamport) via failureReason on the ledger row.
          await update(item.clientMutationId, {
            status: 'failed',
            failureReason: 'photo_not_ready',
          });
          console.warn('[serviceQueue] drain: entry failed — photo not ready', {
            clientMutationId: item.clientMutationId,
            vehicleId: item.vehicleId,
            status: 'failed',
            httpStatus,
            code,
          });
        } else if (isApiError && err.status >= 400 && err.status < 500 && err.status !== 401) {
          // 4xx (non-auth): terminal — mark failed, won't retry.
          await update(item.clientMutationId, { status: 'failed' });
          console.warn('[serviceQueue] drain: entry permanently failed', {
            clientMutationId: item.clientMutationId,
            vehicleId: item.vehicleId,
            status: 'failed',
            httpStatus,
            code,
          });
        } else {
          // Network / 5xx: revert to pending for later retry.
          await update(item.clientMutationId, { status: 'pending' });
          console.warn('[serviceQueue] drain: entry reverted to pending', {
            clientMutationId: item.clientMutationId,
            vehicleId: item.vehicleId,
            status: 'pending',
            httpStatus,
            code,
          });
        }
      }
    }
  } finally {
    draining = false;
  }
}

/** Exposed for testing — reset the single-flight flag between tests. */
export function _resetDrainFlag(): void {
  draining = false;
}

export interface ServiceQueueResult {
  /** Pending (and failed) queued entries for this vehicle. */
  pending: QueuedEntry[];
  /** Manually trigger a drain (e.g. from a retry button). */
  drain: () => Promise<void>;
}

/**
 * useServiceQueue(vehicleId) — subscribe to the pending queue for a vehicle.
 *
 * Returns queued entries (pending + failed) and re-reads after drains.
 * Mounts drain triggers: online event, visibilitychange/focus, 30s tick.
 */
export function useServiceQueue(vehicleId: string): ServiceQueueResult {
  const [pending, setPending] = useState<QueuedEntry[]>([]);
  const isOnline = useOnlineStatus();
  // Ref is only written inside effects, not during render — avoids the
  // react-hooks/refs lint rule that bans render-time ref mutation.
  const vehicleIdRef = useRef(vehicleId);

  // Keep ref in sync with latest vehicleId (inside effect, not render).
  useEffect(() => {
    vehicleIdRef.current = vehicleId;
  });

  const refresh = useCallback(async () => {
    const items = await listFor(vehicleIdRef.current);
    // Show pending AND failed (user needs to see failed to discard them).
    setPending(items.filter((q) => q.status !== 'inflight'));
  }, []);

  const triggerDrain = useCallback(async () => {
    await drainQueue(vehicleIdRef.current);
    await refresh();
  }, [refresh]);

  // Initial load and whenever vehicleId changes.
  useEffect(() => {
    void refresh();
  }, [vehicleId, refresh]);

  // Re-read immediately on ANY queue mutation (enqueue/update/remove/clear).
  // This is what makes an offline `add()` show up optimistically at once —
  // there's no network event to wait for, so we react to the queue write.
  useEffect(() => subscribe(() => void refresh()), [refresh]);

  // Reconnect drain.
  useEffect(() => {
    if (!isOnline) return;
    void triggerDrain();
  }, [isOnline, triggerDrain]);

  // Robust triggers: online event, visibilitychange, window focus, 30s tick.
  useEffect(() => {
    const onOnline = () => {
      void triggerDrain();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void triggerDrain();
    };
    const onFocus = () => {
      void triggerDrain();
    };

    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    // Periodic tick: every 30s while online and queue might be non-empty.
    const tick = setInterval(() => {
      if (navigator.onLine) void triggerDrain();
    }, 30_000);

    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      clearInterval(tick);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { pending, drain: triggerDrain };
}
